// @flow
import React, { Component, Fragment } from 'react';
import axios, { type Axios } from 'axios';
import ProductContent from 'components/product/ProductContent';
import ProductTags from 'components/product/ProductTags';
import { BaseActions, CommonActions, DisplayActions, ProductsActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { ProductData } from 'store/modules/products';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import ProductPlaceholder from 'components/product/ProductPlaceholder';
import throttle from 'lodash/throttle';
import ProductSeriesInfo from 'components/product/ProductSeriesInfo/ProductSeriesInfo';
import ProductRegulationExcReturn from 'components/product/ProductGuide';
import ProductCategories from 'components/product/ProductCategories/ProductCategories';

type Props = {
  username: ?string,
  urlSlug: ?string,
  product: ProductData,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class ProductViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    ProductsActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    const { username, urlSlug, shouldCancel, currentUserRule, currentUser } = this.props;
    const type = 'NORMAL';
    if (!username || !urlSlug || !type) return;
    try {
      if (shouldCancel) return;
      await ProductsActions.readProduct({
        username,
        urlSlug,
        type,
      });
      if (!this.props.product) return;
      const { id } = this.props.product;
      ProductsActions.getSequences(id);
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      ProductsActions.unloadProduct();
      this.initialize();
    }
  }

  componentWillUnmount() {
    ProductsActions.unloadProduct();
  }

  // onSetToc = (toc: ?(TocItem[])) => {
  //   ProductsActions.setToc(toc);
  // };

  onToggleLike = () => {
    const { product, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!product) return;
    if (product.liked) {
      ProductsActions.unlike(product.id);
    } else {
      ProductsActions.like(product.id);
    }
  };

  onToggleAskRemove = () => {
    ProductsActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { product, history, routerHistory } = this.props;
    ProductsActions.toggleAskRemove();
    if (!product) return;
    try {
      await CommonActions.removeProduct(product.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    setTimeout(() => {
      this.initialize();
    }, 0);
    const { hash } = this.props.location;
    if (hash !== '') {
      ProductsActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  onLogin = () => {
    const { product, currentUser } = this.props;
    if (!currentUser) {
      this.props.history.push(
        `/login?next=/@${product.user.username}/products/${product.url_slug}`,
      );
    }
    return !!currentUser;
  };

  // 상품 정보 새로고침 함수
  refreshProductData = async () => {
    const { username, urlSlug } = this.props;
    const type = 'REFRESH';
    if (!username || !urlSlug || !type) return false;
    
    try {
      // 로딩 상태 표시
      BaseActions.showToast({
        message: '상품 정보를 새로고침 중입니다...',
        type: 'info',
      });
      
      // 상품 정보 다시 불러오기
      await ProductsActions.readProduct({
        username,
        urlSlug,
        type,
      });
      
      // 성공 메시지 표시
      BaseActions.showToast({
        message: '상품 정보가 업데이트되었습니다.',
        type: 'success',
      });
      
      return true;
    } catch (e) {
      console.error('상품 정보 새로고침 오류:', e);
      BaseActions.showToast({
        message: '상품 정보 새로고침에 실패했습니다.',
        type: 'error',
      });
      return false;
    }
  };

  onChannel = async () => {
    window.Tawk_API.toggle()
    const { product, currentUser } = this.props;
    const url = 'https://hooks.slack.com/services/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';
    const data = {
      channel: '#제품상담',
      username: 'cacheby-bot',
      text: `제품상담이 들어 왔습니다. \n 유저명: ${
        currentUser ? currentUser.displayName : '비회원'
      } \n 이메일: ${currentUser ? currentUser.email : '비회원'} \n URL: https://cacheby.com/@${
        product.user.username
      }/products/${product.url_slug}`,
    };
    axios.post(url, JSON.stringify(data), {
      withCredentials: false,
      // eslint-disable-next-line no-shadow
      transformRequest: [
        // eslint-disable-next-line no-shadow
        (data, headers) => {
          delete headers.post['Content-Type'];
          return data;
        },
      ],
    });
  };

  render() {
    const {
      product,
      username,
      currentUserRule,
      currentUsername,
      askRemove,
      logged,
      gradeDiscount,
    } = this.props;
    const { onChannel, onLogin, refreshProductData } = this;

    if (!product) return <ProductPlaceholder />;
    if (!product.user) return <ProductPlaceholder />;
    const plainTextBody = convertToPlainText(product.body);

    const url = `${process.env.BASE_URL}/@${product.user.username}/products/${product.url_slug}`;
    const baseTitle = `${product.title} | 캐시바이`;
    const subject = product.title;

    // partNumber로 키워드 만들기
    let partNumber = [];
    let ldJsonArr = [];
    for (let i = 0; i < product.product_price.length; i += 1) {
      partNumber.push(product.product_price[i].part_number);
      const productId = product.id;
      const productPartNumber = product.product_price[i].part_number;
      const productDescription = product.product_price[i].description;
      const productPrice = (product.product_price[i].price * 1.1).toFixed(0);
      const productUnit = product.product_price[i].unit;
      const productSupplier = product.meta.supplier;
      const productThumbnail = product.thumbnail;
      const productName = productDescription.slice(0, 150).trim();
      const ldJson = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: productName,
        image: productThumbnail,
        description: productDescription,
        manufacturer: productSupplier,
        inProductGroupWithID: productId,
        sku: productPartNumber,
        mpn: productPartNumber,
        size: productUnit,
        brand: {
          '@type': 'Brand',
          name: productSupplier,
        },
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'KRW',
          price: productPrice,
          priceValidUntil: '2027-07-01',
          itemCondition: 'https://schema.org/NewCondition',
          availability: 'https://schema.org/InStock',
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'KR',
            returnPolicyCountry: 'KR',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 14,
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn'
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: productPrice >= 100000 ? 0 : 3850,
              currency: 'KRW'
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: 'KR'
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 1,
                unitCode: 'DAY'
              },
              transitTime: {
                '@type': 'QuantitativeValue',
                minValue: 1,
                maxValue: 5,
                unitCode: 'DAY'
              }
            }
          }
        }
      };
      ldJsonArr.push(ldJson);
    }

    // 헬멧에 들어 갈수 있도록 가공 처리
    const keywords = partNumber.join();
    const ldJsonData = JSON.stringify(ldJsonArr);

    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="title" content={baseTitle} />
          <meta name="subject" content={subject} />
          <meta name="publisher" content="연구원들은 다 아는 쇼핑몰 | 캐시바이" />
          <meta name="author" content="캐시바이" />
          <meta name="robots" content="index,follow" />
          <meta name="keywords" content={keywords} />
          <meta name="description" content={plainTextBody} />
          <meta property="og:url" content={url} />
          <meta property="og:site_name" content="연구원들은 다 아는 쇼핑몰 | 캐시바이" />
          <meta property="og:author" content="캐시바이" />
          <meta property="og:type" content="product" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={plainTextBody} />
          {product.thumbnail && <meta property="og:image" content={product.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={plainTextBody} />
          {product.thumbnail && <meta name="twitter:image" content={product.thumbnail} />}
          {ldJsonArr.length && <script type="application/ld+json">{ldJsonData}</script>}
        </Helmet>
        <ProductCategories categories={product.categories} />
        {product.series && <ProductSeriesInfo series={product.series} username={username || ''} />}
        <ProductContent
          isPrivate={product.is_private}
          title={product.title}
          url={this.props.match.url}
          user={product.user}
          informCopy={this.informCopy}
          onToggleLike={this.onToggleLike}
          likes={product.likes}
          liked={product.liked}
          logged={logged}
          onLogin={onLogin}
          ownProduct={currentUsername === username || currentUserRule === 'admin'}
          id={product.id}
          onAskRemove={this.onToggleAskRemove}
          thumbnail={product.thumbnail}
          body={product.body}
          theme={product.meta.code_theme}
          price={product.meta.price}
          supplier={product.meta.supplier}
          productUrl={product.meta.productUrl}
          productPrice={product.product_price}
          currentUsername={currentUsername}
          onChannel={onChannel}
          username={product.user.username}
          displayName={product.user.display_name}
          tags={product.tags}
          categories={product.categories}
          gradeDiscount={gradeDiscount}
          badges={product.badges}
          refreshProductData={refreshProductData}
        />
        {/* 메틀러 토레도의 경우에만 배송, 교환 안내 제외 */}
        {product.meta.supplier !== 'Mettler Toledo' && <ProductRegulationExcReturn />}
        <QuestionModal
          open={askRemove}
          title="상품 삭제"
          description="이 상품을 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ products, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    currentUserRule: user.user && user.user.rule,
    currentUser: user.user,
    product: products.product,
    likeInProcess: pender.pending['products/LIKE'] || pender.pending['products/UNLIKE'],
    askRemove: products.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
    gradeDiscount: (user.user && user.user.grade.value) || 0,
  }),
  () => ({}),
)(withRouter(ProductViewer));



// WEBPACK FOOTER //
// ./src/containers/product/ProductViewer.js